/**
 * Utilidades para contar planificación en la tabla de necesidades por horas.
 * Solo cuentan intervalos con time_type.counts_as_total_hours (work_count en Plan.js).
 */

import { convertFromUTC, isFractionalShift } from "utils/datetime";

export const normalizeStaffingTimeLocal = (timeString) => {
	if (!timeString) return "";
	const padded = timeString.length === 4 ? `0${timeString}` : timeString;
	const [hh, mm = "00"] = padded.split(":");
	return `${hh.padStart(2, "0")}:${mm}`;
};

/** Horas con needed > 0 en el backend, por día calendario (`YYYY-MM-DD_H`). */
export const buildStaffingNeedHoursByDay = (staffingLevelHourlyAssignments) => {
	const map = new Set();

	(staffingLevelHourlyAssignments || []).forEach((assignment) => {
		(assignment.counters || []).forEach((day) => {
			(day.hours || []).forEach((hourEntry) => {
				const needed = Number(hourEntry.needed) || 0;
				if (needed <= 0 || !hourEntry.hour) return;

				const local = convertFromUTC(hourEntry.hour);
				if (!local) return;

				const hourNum = parseInt(local.split(":")[0], 10);
				if (Number.isFinite(hourNum)) {
					map.add(`${day.day}_${hourNum}`);
				}
			});
		});
	});

	return map;
};

const findNeedEntryInDayHours = (dayHours, timeString) => {
	const normalized = normalizeStaffingTimeLocal(timeString);
	if (!dayHours?.length) return null;

	return (
		dayHours.find((h) => {
			const local = convertFromUTC(h.hour);
			return local === normalized || local === timeString;
		}) || null
	);
};

/**
 * Busca la necesidad de un cuarto de hora. Si el día siguiente abre columna 0h,
 * las necesidades de madrugada pueden venir en el counter del día anterior (00:XX).
 */
export const resolveStaffingNeedEntry = ({
	day,
	dayIndex,
	planDays,
	timeString,
	functionCounters,
}) => {
	const direct = findNeedEntryInDayHours(day.hours, timeString);
	if (direct) return direct;

	const normalized = normalizeStaffingTimeLocal(timeString);
	const hourNum = parseInt(normalized.split(":")[0], 10);
	if (!Number.isFinite(hourNum) || hourNum > 1 || dayIndex <= 0) return null;

	const prevDay = planDays?.[dayIndex - 1]?.day;
	if (!prevDay) return null;

	const prevCounter = (functionCounters || []).find((c) => c.day === prevDay);
	return findNeedEntryInDayHours(prevCounter?.hours, timeString);
};

export const getVisualGapHoursForDay = (planAssignments, dayIndex) =>
	planAssignments?.[0]?.plans?.[dayIndex]?.visual_gap_hours || [];

export const isVisualPaddingHour = (
	visualGapHours,
	staffingNeedHoursByDay,
	dayKey,
	hourNum
) =>
	visualGapHours.includes(hourNum) &&
	!staffingNeedHoursByDay.has(`${dayKey}_${hourNum}`);

export const shouldCountInStaffingNeeds = (hourAssignment) => {
	if (!hourAssignment?.assignment) return false;
	if (hourAssignment.isAvailability) return false;
	return (Number(hourAssignment.work_count) || 0) > 0;
};

export const getStaffingNeedsCoverage = (hourAssignment) => ({
	startOffset: Number(hourAssignment.start_offset) || 0,
	duration: Number(hourAssignment.work_count) || 0,
});

export const isQuarterCoveredByStaffingWork = (
	quarterStart,
	quarterEnd,
	startOffset,
	duration
) => {
	if (duration <= 0) return false;
	const turnEnd = startOffset + duration;
	return quarterStart < turnEnd && quarterEnd > startOffset;
};

export const intervalCountsInStaffingNeeds = (interval) =>
	Boolean(interval?.time_type?.counts_as_total_hours);

/**
 * Intervalos que definen una fila de necesidad.
 * Un need trae turno o `time_interval`, nunca los dos.
 */
export const getStaffingAssignmentIntervals = (assignment) => {
	if (assignment?.shift?.time_intervals?.length) {
		return assignment.shift.time_intervals;
	}
	if (assignment?.time_interval?.start && assignment?.time_interval?.end) {
		return [assignment.time_interval];
	}
	return [];
};

/**
 * Una necesidad con `time_interval` es fraccionada: no depende de un turno.
 * Las de turno siguen la regla de duración (≤ 60 min y múltiplo de 15).
 */
export const isFractionalStaffingAssignment = (assignment) => {
	if (!assignment) return false;
	if (!assignment.shift && assignment.time_interval) return true;
	return isFractionalShift(assignment.shift);
};

/**
 * Intervalos que este assignment pinta en el día que se está recorriendo.
 *
 * El orden importa: un turno que cruza medianoche deja `time_intervals_for_day`
 * con la rebanada del día de origen y genera una copia con
 * `time_intervals_overnight` en el día siguiente. Usar `time_intervals` en ese
 * caso contaría la madrugada dos veces (en el día de origen y en el siguiente).
 */
export const getAssignmentEffectiveIntervals = (assignment) => {
	if (!assignment) return [];
	if (assignment.time_intervals_overnight?.length) {
		return assignment.time_intervals_overnight;
	}
	if (assignment.time_intervals_for_day?.length) {
		return assignment.time_intervals_for_day;
	}
	if (assignment.time_intervals_from_shift) {
		return assignment.shift?.time_intervals || [];
	}
	return assignment.time_intervals || [];
};

export const addStaffingCoverageToMap = (
	quarterHourMap,
	dayKey,
	functionName,
	employee,
	quarterHourTime
) => {
	const empId = employee?.id;
	const empName =
		`${employee?.name || ""} ${employee?.surname || ""}`.trim() ||
		"Empleado sin nombre";
	if (empId === undefined) return;

	const upsert = (key) => {
		if (!quarterHourMap.has(key)) {
			quarterHourMap.set(key, { peopleSet: new Set(), people: [] });
		}
		const data = quarterHourMap.get(key);
		if (!data.peopleSet.has(empId)) {
			data.peopleSet.add(empId);
			data.people.push(empName);
		}
	};

	upsert(`${dayKey}_ALL_${quarterHourTime}`);
	if (functionName) {
		upsert(`${dayKey}_${functionName}_${quarterHourTime}`);
	}
};

/**
 * Enumera los cuartos de hora locales (HH:mm) que abarca un intervalo de turno.
 * Convierte desde UTC porque las claves del mapa de cobertura están en hora local.
 * Cuenta solapamiento parcial: cualquier cuarto que toque el intervalo se incluye.
 *
 * `clampToDay` corta en medianoche: al indexar la planificación, la madrugada
 * pertenece al día siguiente (que ya recibe su propia copia overnight), mientras
 * que una necesidad fraccionada de 23:45 a 00:15 sí debe envolver dentro de su día.
 */
const enumerateLocalQuarters = (timeIntervals, { clampToDay = false } = {}) => {
	const quarters = new Set();
	if (!Array.isArray(timeIntervals)) return quarters;

	timeIntervals.forEach((interval) => {
		if (!interval?.start || !interval?.end) return;

		const localStart = convertFromUTC(interval.start);
		const localEnd = convertFromUTC(interval.end);
		if (!localStart || !localEnd) return;

		const [sh, sm] = localStart.split(":").map(Number);
		const [eh, em] = localEnd.split(":").map(Number);
		let startMin = sh * 60 + sm;
		let endMin = eh * 60 + em;
		if (endMin <= startMin) endMin += 1440; // intervalo que cruza medianoche
		if (clampToDay) endMin = Math.min(endMin, 1440);

		// Primer cuarto que solapa: redondear el inicio hacia abajo a 15 min.
		let cursor = Math.floor(startMin / 15) * 15;
		while (cursor < endMin) {
			const localMin = cursor % 1440;
			const hh = Math.floor(localMin / 60)
				.toString()
				.padStart(2, "0");
			const mm = (localMin % 60).toString().padStart(2, "0");
			quarters.add(`${hh}:${mm}:00Z`);
			cursor += 15;
		}
	});

	return quarters;
};

/**
 * Construye el mapa de cobertura por cuarto de hora a partir de la planificación.
 *
 * Cada entrada se indexa por `${day}_${functionName}_${HH:mm:00Z}` (y una clave
 * `_ALL_` con todos los empleados) y guarda el conjunto de empleados distintos
 * planificados que cubren ese cuarto de hora.
 *
 * Lee `plans[].plan_assignments_with_overnight`, que es la lista de turnos que
 * pintan cada día (incluidas las rebanadas que vienen del día anterior). No usa
 * `plan_assignments_hourly_totals`: esa rejilla horaria se salta en la carga
 * paginada fuera de la vista Horas, y con ella la cobertura salía siempre a 0.
 *
 * @param {Array} planAssignments - this.state.plan_assignments
 * @returns {Map<string, { peopleSet: Set, people: string[] }>}
 */
export const buildQuarterHourCoverageMap = (planAssignments) => {
	const quarterHourMap = new Map();

	if (!Array.isArray(planAssignments) || planAssignments.length === 0) {
		return quarterHourMap;
	}

	planAssignments.forEach((pa) => {
		(pa.plans || []).forEach((plan) => {
			const dayKey = plan.day;
			if (!dayKey) return;

			const dayAssignments =
				plan.plan_assignments_with_overnight || plan.assignments || [];

			dayAssignments.forEach((assignment) => {
				// Solo cuentan los intervalos productivos: es el mismo criterio que
				// aplicaba work_count (disponibilidad, ausencias y descansos fuera).
				const intervals = getAssignmentEffectiveIntervals(assignment).filter(
					intervalCountsInStaffingNeeds
				);
				if (!intervals.length) return;

				const employee =
					assignment.employee_with_ubication ||
					assignment.employee ||
					pa.employee;
				const functionName = assignment.function?.name;

				enumerateLocalQuarters(intervals, { clampToDay: true }).forEach(
					(quarterHourTime) => {
						addStaffingCoverageToMap(
							quarterHourMap,
							dayKey,
							functionName,
							employee,
							quarterHourTime
						);
					}
				);
			});
		});
	});

	return quarterHourMap;
};

/**
 * Calcula la cobertura de empleados planificados para una necesidad fraccionada
 * en un día concreto.
 *
 * Un empleado cuenta si está planificado en la misma función y solapa (parcial o
 * totalmente) cualquiera de los cuartos de hora del intervalo de la fracción.
 *
 * @param {Object} params
 * @param {Map} params.quarterHourMap - mapa de buildQuarterHourCoverageMap
 * @param {string} params.dayKey - día (YYYY-MM-DD)
 * @param {string} params.functionName - nombre de la función de la necesidad
 * @param {Array} params.timeIntervals - intervalos del turno fraccionado (UTC)
 * @returns {{ current: number, people: string[] }}
 */
export const getFractionalNeedCoverage = ({
	quarterHourMap,
	dayKey,
	functionName,
	timeIntervals,
}) => {
	if (!quarterHourMap || !dayKey || !functionName) {
		return { current: 0, people: [] };
	}

	const quarters = enumerateLocalQuarters(timeIntervals);
	const seen = new Set();
	const people = [];

	quarters.forEach((quarterHourTime) => {
		const data = quarterHourMap.get(
			`${dayKey}_${functionName}_${quarterHourTime}`
		);
		if (!data) return;

		// peopleSet (ids) y people (nombres) se rellenan en paralelo en
		// addStaffingCoverageToMap, así que comparten índice.
		const ids = data.peopleSet ? [...data.peopleSet] : [];
		(data.people || []).forEach((person, idx) => {
			const key = ids[idx] !== undefined ? ids[idx] : person;
			if (!seen.has(key)) {
				seen.add(key);
				people.push(person);
			}
		});
	});

	return { current: seen.size, people };
};

/** Valor efectivo de necesidad: usa overwrite solo si está definido. */
export const getEffectiveNeeded = (counter) =>
	counter?.overwrite != null ? counter.overwrite : counter?.needed;

/** Indica si el contador tiene un overwrite manual distinto del valor base. */
export const isStaffingOverwrite = (counter) =>
	counter?.overwrite != null && counter.overwrite !== counter?.needed;
